import React from 'react';

import cx from 'classnames';
import { addYears } from 'date-fns';
import { isNil, isEmpty } from 'ramda';
import { Field, Form as FinalForm } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNetworkState } from 'react-use';

import { Alert } from 'components/shared/Alert';
import ArrayFields from 'components/shared/ArrayFields';
import { BaseInput } from 'components/shared/BaseFormComponents/Input';
import { BaseSelect } from 'components/shared/BaseFormComponents/Select';
import Button from 'components/shared/Button';
import Divider from 'components/shared/Divider';
import { InputDatePicker } from 'components/shared/FormAdapters/FormDefault/datePicker/inputs';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import { withFinalForm } from 'components/shared/FormAdapters/FormDefault/select/withFinalForm';
import Loader from 'components/shared/Loader';
import { PlacesAsyncSelect } from 'components/shared/SelectRB/custom';
import FormTitle from 'components/shared/Typography/FormTitle';
import Text from 'components/shared/Typography/Text';
import UnsavedChangesFormWatcher from 'components/shared/UnsavedChangesFormWatcher';
import { MODAL_TYPE } from 'constants/modalTypes';
import { useGoogleMaps } from 'hooks/useGoogleMaps';
import useValidationSchema from 'hooks/useValidationSchema';
import i18n from 'i18n';
import { itinerariesItemSettingsSelector } from 'store/itinerariesItem/selectors';
import { setTermsOfServiceModalOpenAction } from 'store/terms_of_use';
import { getDateFormat } from 'utils/date';
import { scrollToErrorOnSubmitDecorator } from 'utils/form/decorators';
import { getGuestWeightOptions } from 'utils/itineraries/guestPortal/guests';
import { isNotDefined } from 'utils/ramda';

import { TravelWalletDocumentTypes, TravelWalletFileKeys } from '../../TravelWallet/constansts';
import { FormUpdateWatcher } from '../FormUpdateWatcher';
import { useCountries } from '../functions';
import { i18nOptions } from './constants';
import { geocodingDecorator, getAddressComponents, getStateFromGeocoding } from './decorators';
import { checkHasPassportExpirationDateWarning, getTravelWalletDocumentsLocalisation } from './functions';
import styles from './index.module.css';
import { DocumentsAdapter as DefaultDocumentsAdapter } from './Upload/DocumentsAdapter';
import { getValidationSchema } from './validationSchema';

const PlacesAsyncSelectAdapter = withFinalForm(PlacesAsyncSelect);

const emergencyContacts = 'emergencyContacts';

function GuestForm(props) {
  const { guest, onSubmit, isLanding } = props;
  const validationSchema = getValidationSchema(isLanding);
  const validate = useValidationSchema(validationSchema);
  useGoogleMaps();

  return (
    <FinalForm
      onSubmit={onSubmit}
      initialValues={guest}
      validate={validate}
      decorators={[scrollToErrorOnSubmitDecorator, geocodingDecorator]}
      mutators={{
        setDob: (args, state, utils) => {
          utils.changeValue(state, 'dob', () => args[0].start);
        },
        setPassportExpirationDate: (args, state, utils) => {
          utils.changeValue(state, 'passportExpirationDate', () => args[0].start);
        },
      }}
    >
      {(formProps) => <Form {...props} {...formProps} />}
    </FinalForm>
  );
}

const Form = ({
  handleSubmit,
  submitting,
  values,
  form,
  errors,

  guest,
  onFormDataUpdated,
  isLanding,
  saveAndNotify,
  travelEndDate,
}) => {
  const settings = useSelector(itinerariesItemSettingsSelector);
  const { countries, states, onCountryChange } = useCountries(guest?.country);
  const { online } = useNetworkState();
  const [showManualFields, setShowManualFields] = React.useState(false);

  const isVisibleSaveAndNotifyBtn = guest && !isEmpty(guest) && saveAndNotify;
  // Show date input value as DD/MM/YYYY only if setting "displayDatesDdMmYyyy" is on and its landing (client-facing)
  const datePickerFormat = getDateFormat(settings?.displayDatesDdMmYyyy && isLanding);

  const documentsLocalisation = getTravelWalletDocumentsLocalisation();

  const DocumentsAdapter = React.useCallback(
    ({ input }) => {
      const { name, value, onChange } = input;

      const fileCategory = TravelWalletDocumentTypes?.[name];
      const documentsType = documentsLocalisation?.[name];

      return (
        <DefaultDocumentsAdapter
          value={value}
          onChange={onChange}
          fileCategory={fileCategory}
          documentsType={documentsType}
          buttonActionType={i18n.t('files.upload', i18nOptions)}
          disabled={!online}
          placeholder={i18n.t('proposal_content.guest_portal.file.no_documents')}
          uploadAlertText={i18n.t('proposal_content.file.info_description')}
        />
      );
    },
    [i18nOptions, online],
  );

  const isNotAvailableForSubmitting = submitting || !online;
  const hasPassportExpirationDateWarning = checkHasPassportExpirationDateWarning(
    values?.passportExpirationDate,
    travelEndDate,
  );

  const handleSaveAndNotify = async () => {
    await handleSubmit();
    if (isNotDefined(errors)) await saveAndNotify();
  };

  const geocodeFieldValue = values?.geocode;
  const countryFieldValue = values?.country;

  React.useEffect(() => {
    // should be used in useEffect to trigger on form geocodingDecorator changes
    onCountryChange(countryFieldValue);
  }, [countryFieldValue]);

  React.useEffect(() => {
    if (isNotDefined(geocodeFieldValue)) return;
    const state = getStateFromGeocoding(geocodeFieldValue);
    const stateOption = states?.find((s) => s.value === state || s.label === state);

    form.change('state', stateOption?.value);
  }, [states, geocodeFieldValue]);

  React.useEffect(() => {
    const watchedValues = ['address', 'country', 'city', 'state', 'zip'];
    const hasAnyValue = watchedValues.some((key) => !!values?.[key]);
    if (hasAnyValue) setShowManualFields(true);
  }, [values]);

  React.useEffect(() => {
    if (!values?.geocode) return;
    const { address, country, city, geocodeState, zipCode } = getAddressComponents(values.geocode);

    const isCountryEquals = values.country == country;
    const isAddressEquals = values.address === address;
    const isZipCodeEquals = values.zip === zipCode;
    const isCityEquals = values.city === city;

    const stateOption = states?.find((s) => s.value === geocodeState || s.label === geocodeState);
    const isStateEquals = !values.state || values.state === stateOption?.label || values.state === stateOption?.value;

    const isSame = isCountryEquals && isStateEquals && isAddressEquals && isCityEquals && isZipCodeEquals;

    if (!isSame) {
      form.change('place', undefined);
      form.change('geocode', undefined);
    }
  }, [values, states]);

  return (
    <form className={cx(styles.guestForm, { [styles['guestForm-disabled']]: !online })} autoComplete="nope">
      {onFormDataUpdated && <FormUpdateWatcher onUpdate={onFormDataUpdated} />}

      <UnsavedChangesFormWatcher />
      {submitting && <Loader isFillBlock />}

      <FormTitle className={styles.sectionTitle} noBorderWithMargin>
        {i18n.t('guest_information.title', i18nOptions)}
      </FormTitle>
      <Field component={FormGroupAdapter} name="guestInfo" type="inputGroup">
        <div className={styles.threeColumns}>
          <Field
            component={FormGroupAdapter}
            name="firstName"
            label={i18n.t('common.inputs.first_name') + '*'}
            type="text"
            parse={(value) => value || null}
            customComponent={BaseInput}
            isLabelInside
          />
          <Field
            component={FormGroupAdapter}
            name="middleName"
            label={i18n.t('guest_information.input.middle_name', i18nOptions)}
            type="text"
            parse={(value) => value || null}
            customComponent={BaseInput}
            isLabelInside
          />
          <Field
            component={FormGroupAdapter}
            name="lastName"
            label={i18n.t('common.inputs.last_name') + '*'}
            type="text"
            parse={(value) => value || null}
            customComponent={BaseInput}
            isLabelInside
          />
          <Field
            component={FormGroupAdapter}
            name="preferredName"
            label={i18n.t('guest_information.input.preferred_name', i18nOptions)}
            type="text"
            parse={(value) => value || null}
            customComponent={BaseInput}
            isLabelInside
          />
          <Field
            component={FormGroupAdapter}
            name="email"
            label={i18n.t('common.inputs.email')}
            type="text"
            parse={(value) => value || null}
            customComponent={BaseInput}
            isLabelInside
          />
          <Field
            component={FormGroupAdapter}
            name="phone"
            label={i18n.t('common.inputs.phone_number')}
            type="tel"
            parse={(value) => value || null}
            customComponent={BaseInput}
            isLabelInside
          />
          <Field
            component={FormGroupAdapter}
            name="mobile"
            label={i18n.t('common.inputs.mobile_number')}
            type="tel"
            parse={(value) => value || null}
            customComponent={BaseInput}
            isLabelInside
          />

          <Field
            name="dob"
            render={(fieldProps) => (
              <InputDatePicker
                {...fieldProps}
                mode="single"
                closeOnSelect
                startMonth={new Date(1900, 0)}
                endMonth={new Date()}
                disabledDates={{ after: new Date() }}
                clearable
                position="BOTTOM_LEFT"
              >
                <InputDatePicker.NumericalDatePlank dateFormat={datePickerFormat} input={fieldProps.input} />
              </InputDatePicker>
            )}
            placeholder={i18n.t('guest_information.input.date_of_birth', i18nOptions)}
          />
        </div>
      </Field>

      <FormTitle className={styles.sectionTitle} noBorderWithMargin>
        {i18n.t('guest_address.title', i18nOptions)}
      </FormTitle>

      <Field name="place">
        {(fieldProps) => (
          <FormGroupAdapter
            className={cx(showManualFields && styles.geocodingContainer)}
            noMargin
            withChildren
            {...fieldProps}
          >
            <PlacesAsyncSelectAdapter isClearable {...fieldProps} />
          </FormGroupAdapter>
        )}
      </Field>

      {!showManualFields && (
        <div className={styles.linkTextContainer}>
          <Text type="brandon" className={styles.linkText} onClick={() => setShowManualFields(true)}>
            Or, add address manually
          </Text>
        </div>
      )}

      {showManualFields && (
        <Field component={FormGroupAdapter} name="guestAddress" type="inputGroup">
          <div className={styles.addressFieldsWrapper}>
            <div className={styles.twoColumns}>
              <Field
                component={FormGroupAdapter}
                name="country"
                type="select"
                options={countries}
                placeholder={i18n.t('guest_address.input.country', i18nOptions)}
                isClearable
                onCustomChange={() => {
                  form.change('state', null);
                }}
                customComponent={BaseSelect}
                isCreatable={false}
              />
              <Field
                component={FormGroupAdapter}
                name="address"
                label={i18n.t('guest_address.input.address', i18nOptions)}
                type="text"
                customComponent={BaseInput}
                parse={(value) => value || null}
                isLabelInside
              />
            </div>
            <div className={styles.threeColumns}>
              <Field
                component={FormGroupAdapter}
                name="city"
                label={i18n.t('guest_address.input.city', i18nOptions)}
                type="text"
                customComponent={BaseInput}
                parse={(value) => value || null}
                isLabelInside
              />
              <Field
                component={FormGroupAdapter}
                name="state"
                type="select"
                placeholder={i18n.t('guest_address.input.state', i18nOptions)}
                options={states}
                readOnly={isNil(values.country)}
                isClearable
                customComponent={BaseSelect}
                isCreatable={false}
                className={cx({ [styles.disabled]: isNil(values.country) })}
              />
              <Field
                component={FormGroupAdapter}
                name="zip"
                label={i18n.t('guest_address.input.zip', i18nOptions)}
                type="text"
                inputMode="numeric"
                parse={(value) => value || null}
                customComponent={BaseInput}
                isLabelInside
              />
            </div>
          </div>
        </Field>
      )}
      <FormTitle className={styles.sectionTitle} noBorderWithMargin>
        {i18n.t('health_and_diet.title', i18nOptions)}
      </FormTitle>
      <Field component={FormGroupAdapter} name="weightInfo" type="inputGroup">
        <div className={styles.twoColumns}>
          <Field
            component={FormGroupAdapter}
            name="weight"
            label={i18n.t('health_and_diet.input.weight', i18nOptions)}
            type="number"
            inputMode="numeric"
            guide={false}
            parse={(v) => (Number(v) ? Number(v) : null)}
            customComponent={BaseInput}
            isLabelInside
          />

          <Field
            component={FormGroupAdapter}
            name="weightUnit"
            placeholder={i18n.t('health_and_diet.input.weight_unit', i18nOptions)}
            type="select"
            options={getGuestWeightOptions()}
            isClearable
            customComponent={BaseSelect}
            isCreatable={false}
          />
        </div>
      </Field>

      <Field
        component={FormGroupAdapter}
        name="medicalIssues"
        placeholder={i18n.t('health_and_diet.input.medical_issues.placeholder', i18nOptions)}
        type="textarea"
        minRows={2}
        label={i18n.t('health_and_diet.input.medical_issues.title', i18nOptions)}
        noTransform
        parse={(value) => value || null}
      />
      <Field
        component={FormGroupAdapter}
        name="dietaryPreferences"
        placeholder={i18n.t('health_and_diet.input.dietary_preferences.placeholder', i18nOptions)}
        type="textarea"
        minRows={2}
        label={i18n.t('health_and_diet.input.dietary_preferences.title', i18nOptions)}
        noTransform
        parse={(value) => value || null}
      />
      <Field
        component={FormGroupAdapter}
        name="drinksPreferences"
        placeholder={i18n.t('health_and_diet.input.drinks_preferences.placeholder', i18nOptions)}
        type="textarea"
        minRows={2}
        label={i18n.t('health_and_diet.input.drinks_preferences.title', i18nOptions)}
        noTransform
        parse={(value) => value || null}
      />

      <FormTitle className={styles.sectionTitle} noBorderWithMargin>
        {i18n.t('passport_information.title', i18nOptions)}
      </FormTitle>

      <div className={styles.twoColumns}>
        <Field
          component={FormGroupAdapter}
          name="passportNumber"
          label={i18n.t('passport_information.input.passport_number', i18nOptions)}
          type="text"
          customComponent={BaseInput}
          parse={(value) => value || null}
          isLabelInside
        />

        <div className={styles.datePickerContainer}>
          <Field
            name="passportExpirationDate"
            render={(fieldProps) => (
              <InputDatePicker
                {...fieldProps}
                mode="single"
                closeOnSelect
                startMonth={new Date()}
                endMonth={addYears(new Date(), 20)}
                disabledDates={{ before: new Date() }}
                clearable
                position="BOTTOM_LEFT"
              >
                <InputDatePicker.NumericalDatePlank dateFormat={datePickerFormat} input={fieldProps.input} />
              </InputDatePicker>
            )}
            placeholder={i18n.t('passport_information.input.passport_expiry', i18nOptions)}
          />
        </div>
      </div>

      {hasPassportExpirationDateWarning && (
        <Alert
          variant="warning"
          content={i18n.t('passport_information.input.warning', i18nOptions)}
          className={styles.alert}
        />
      )}

      <Field
        name={TravelWalletFileKeys.passportDocuments}
        component={({ input }) => <DocumentsAdapter input={input} />}
      />

      <FormTitle className={styles.sectionTitle} noBorderWithMargin>
        {i18n.t('travel_insurance.title', i18nOptions)}
      </FormTitle>

      <Field
        component={FormGroupAdapter}
        name="travelInsuranceInfo"
        placeholder={i18n.t('travel_insurance.input.travel_insurance_info', i18nOptions)}
        label={i18n.t('travel_insurance.description', i18nOptions)}
        type="textarea"
        minRows={2}
        parse={(value) => value || null}
      />

      <Field
        name={TravelWalletFileKeys.insuranceDocuments}
        component={({ input }) => <DocumentsAdapter input={input} />}
      />

      <FormTitle className={styles.sectionTitle} noBorderWithMargin hint={i18n.t('other.description', i18nOptions)}>
        {i18n.t('other.title', i18nOptions)}
      </FormTitle>

      <Field
        name={TravelWalletFileKeys.guestMiscDocuments}
        component={({ input }) => <DocumentsAdapter input={input} />}
      />

      <FormTitle className={styles.sectionTitle} noBorderWithMargin>
        {i18n.t('misc.title', i18nOptions)}
      </FormTitle>
      <Field
        component={FormGroupAdapter}
        name="miscRoomingInfo"
        placeholder={i18n.t('misc.input.rooming', i18nOptions)}
        label={i18n.t('misc.description', i18nOptions)}
        type="textarea"
        minRows={2}
        parse={(value) => value || null}
      />

      <FormTitle className={styles.sectionTitle} noBorderWithMargin>
        {i18n.t('emergency_contact.title', i18nOptions)}
      </FormTitle>

      <div className={styles.arrayFieldsWrapper}>
        <ArrayFields
          initialValues={guest}
          data={{
            sectionName: emergencyContacts,
            fieldsData: [
              {
                name: 'name',
                placeholder: i18n.t('emergency_contact.input.name', i18nOptions),
              },
              {
                name: 'phone',
                placeholder: i18n.t('common.inputs.phone_number'),
              },
            ],
          }}
        />
      </div>

      {isLanding && (
        <>
          <Divider />
          <Field
            component={FormGroupAdapter}
            name="sharePersonalInformation"
            type="checkbox"
            labelText={<AgreementLabel translationOptions={i18nOptions} />}
            titleClassName={styles.labelText}
            parse={(value) => value || null}
            withBackground
            defaultValue={values?.sharePersonalInformation}
            className={styles.margin}
          />
        </>
      )}

      <FormActions className={styles.footer} isRight>
        {isVisibleSaveAndNotifyBtn && (
          <Button
            type="primary2"
            size="large"
            htmlType="button"
            disabled={isNotAvailableForSubmitting}
            onClick={handleSaveAndNotify}
          >
            {i18n.t('save_and_notify_consultant', i18nOptions)}
          </Button>
        )}
        <Button htmlType="button" onClick={handleSubmit} size="large" disabled={isNotAvailableForSubmitting}>
          {i18n.t('common.buttons.save')}
        </Button>
      </FormActions>
    </form>
  );
};

const AgreementLabel = ({ translationOptions }) => {
  const dispatch = useDispatch();

  return (
    <div>
      <Text>{i18n.t('checkbox.share_personal_information_part1', translationOptions)}</Text>{' '}
      <Text
        className={cx('link', styles.link)}
        onClick={() => dispatch(setTermsOfServiceModalOpenAction(MODAL_TYPE.PRIVACY_POLICY))}
      >
        {i18n.t('checkbox.share_personal_information_privacy_policy', translationOptions)}
      </Text>
      <Text>{i18n.t('checkbox.share_personal_information_part3', translationOptions)}</Text>
    </div>
  );
};

export default GuestForm;
